<template>
  <section class="order-result-page py-5 mt-5">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-6">
          <div class="result-card">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <h5>{{ verifyingMp ? 'Verificando tu pago…' : 'Cargando pedido…' }}</h5>
            </div>

            <div v-else-if="paymentStatus === 'success'" class="text-center py-4">
              <i class="fas fa-check-circle text-success result-icon mb-3"></i>
              <h2 class="text-success mb-2">{{ successTitle }}</h2>
              <p class="text-muted mb-4">{{ successMessage }}</p>
              <div v-if="orderDetails" class="order-box text-start mb-4">
                <div><strong>Pedido:</strong> #{{ orderDetails.id }}</div>
                <div><strong>Total:</strong> {{ formatPrice(orderDetails.total) }}</div>
                <div v-if="orderDetails.payment_method_display" class="mt-1">
                  <strong>Pago:</strong> {{ orderDetails.payment_method_display }}
                </div>
              </div>
              <div class="alert alert-info small">
                <i class="fas fa-envelope me-1"></i>
                Te enviamos un email con el detalle del pedido.
              </div>
              <div class="d-grid gap-2 d-md-flex justify-content-md-center mt-3">
                <router-link
                  v-if="orderDetails?.id"
                  :to="orderDetailLink"
                  class="btn btn-primary"
                >
                  Ver detalle del pedido
                </router-link>
                <router-link :to="ordersLink" class="btn btn-outline-light">Mis pedidos</router-link>
                <router-link :to="shopLink" class="btn btn-outline-light">Seguir comprando</router-link>
              </div>
            </div>

            <div v-else-if="paymentStatus === 'pending'" class="text-center py-4">
              <i class="fas fa-clock text-warning result-icon mb-3"></i>
              <h2 class="text-warning mb-2">Pago pendiente</h2>
              <p class="text-muted">Estamos confirmando tu pago con Mercado Pago.</p>
              <button class="btn btn-primary mt-3" :disabled="loading" @click="checkMpStatus">
                Reintentar verificación
              </button>
            </div>

            <div v-else class="text-center py-4">
              <i class="fas fa-times-circle text-danger result-icon mb-3"></i>
              <h2 class="text-danger mb-2">Pago no confirmado</h2>
              <p class="text-muted mb-4">{{ errorMessage || 'El pago fue rechazado o cancelado.' }}</p>
              <router-link :to="ordersLink" class="btn btn-primary">Ir a mis pedidos</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { checkMercadoPagoStatus } from '@/services/payment'
import { getOrderDetails } from '@/services/orders'
import { isIngresoHost, ingresoDefaultRoute } from '@/utils/host'

const route = useRoute()
const loading = ref(true)
const verifyingMp = ref(false)
const paymentStatus = ref('pending')
const orderDetails = ref(null)
const errorMessage = ref('')
const ordersLink = isIngresoHost() ? '/ingreso/pedidos' : '/panel/mis-pedidos'
const shopLink = isIngresoHost() ? ingresoDefaultRoute() : '/productos'

const orderDetailLink = computed(() => {
  const id = orderDetails.value?.id || route.params.orderId
  return isIngresoHost() ? `/ingreso/pedidos/${id}` : `/panel/mis-pedidos/${id}`
})

const formatPrice = (price) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(price || 0)

const usesMercadoPago = computed(() => {
  const pm = orderDetails.value?.payment_method
  return pm === 'online'
})

const successTitle = computed(() =>
  usesMercadoPago.value ? '¡Pago exitoso!' : '¡Pedido confirmado!'
)

const successMessage = computed(() =>
  usesMercadoPago.value
    ? 'Tu pedido fue confirmado correctamente.'
    : 'Registramos tu pedido. Te contactaremos para coordinar el pago y la entrega.'
)

const checkMpStatus = async () => {
  const orderId = route.params.orderId
  verifyingMp.value = true
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await checkMercadoPagoStatus(orderId)
    if (result.status === 'not_applicable') {
      paymentStatus.value = 'success'
      return
    }
    if (result.status === 'approved' || result.payment_status === 'approved') {
      paymentStatus.value = 'success'
    } else if (result.status === 'pending' || result.payment_status === 'pending') {
      paymentStatus.value = 'pending'
    } else {
      paymentStatus.value = 'failure'
      errorMessage.value = result.message || result.detail || result.error
    }
  } catch (e) {
    const data = e.response?.data
    if (data?.status === 'not_applicable') {
      paymentStatus.value = 'success'
      return
    }
    paymentStatus.value = 'failure'
    errorMessage.value = data?.error || data?.detail || data?.message || e.message
  } finally {
    loading.value = false
    verifyingMp.value = false
  }
}

onMounted(async () => {
  loading.value = true
  try {
    const orderId = route.params.orderId
    orderDetails.value = await getOrderDetails(orderId)
    if (usesMercadoPago.value) {
      await checkMpStatus()
    } else {
      paymentStatus.value = 'success'
      loading.value = false
    }
  } catch (e) {
    paymentStatus.value = 'failure'
    errorMessage.value = e.response?.data?.detail || e.message || 'No se pudo cargar el pedido'
    loading.value = false
  }
})
</script>

<style scoped>
.order-result-page{
  font-family: 'Poppins', sans-serif;
}
.result-card {
  background: var(--grey-color);
  border: 1px solid var(--black-color-2);
  border-radius: 18px;
  padding: 2rem;
  color: var(--white-color);
}

.result-icon {
  font-size: 3.5rem;
}

.order-box {
  background: var(--black-color-2);
  border-radius: 12px;
  padding: 1rem;
}
</style>
